import { useCallback, useEffect, useMemo, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { fmtCFA } from "@/lib/format";
import { toast } from "sonner";

export const PAY_LABELS = { especes: "Espèces", wave: "Wave", orange_money: "Orange Money" };
export const payLabel = (p) => PAY_LABELS[p] || p;

const sameLine = (a, b) => a.type === b.type && a.ref_id === b.ref_id && a.nom === b.nom && (a.membre_id || null) === (b.membre_id || null);

export function useCaisse() {
  const [data, setData] = useState(null);
  const [produits, setProduits] = useState([]);
  const [seances, setSeances] = useState([]);
  const [cart, setCart] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const [c, p, s] = await Promise.all([api.get("/caisse/aujourdhui"), api.get("/produits"), api.get("/seances")]);
    setData(c.data);
    setProduits(p.data);
    setSeances(s.data);
  }, []);

  useEffect(() => {
    refresh().catch(() => toast.error("Erreur de chargement de la caisse"));
  }, [refresh]);

  const caisseOuverte = data?.caisse?.statut === "ouverte";
  const total = useMemo(() => cart.reduce((s, i) => s + i.prix * i.quantite, 0), [cart]);

  const addToCart = useCallback((item) => {
    if (!caisseOuverte) return toast.error("Ouvrez la caisse avant de vendre");
    setCart((prev) => {
      const idx = prev.findIndex((i) => sameLine(i, item));
      const inCart = idx >= 0 ? prev[idx].quantite : 0;
      if (item.stockMax != null && inCart + 1 > item.stockMax) {
        toast.error(`Stock maximum atteint pour « ${item.nom} » (${item.stockMax})`);
        return prev;
      }
      if (idx >= 0) return prev.map((i, k) => (k === idx ? { ...i, quantite: i.quantite + 1 } : i));
      return [...prev, { ...item, quantite: 1 }];
    });
  }, [caisseOuverte]);

  const changeQty = useCallback((idx, delta) => {
    setCart((prev) => {
      const target = prev[idx];
      if (delta > 0 && target?.stockMax != null && target.quantite + delta > target.stockMax) {
        toast.error(`Stock maximum atteint pour « ${target.nom} » (${target.stockMax})`);
        return prev;
      }
      return prev.map((i, k) => (k === idx ? { ...i, quantite: i.quantite + delta } : i)).filter((i) => i.quantite > 0);
    });
  }, []);

  const removeLine = useCallback((idx) => setCart((prev) => prev.filter((_, k) => k !== idx)), []);

  const run = useCallback(async (fn) => {
    setBusy(true);
    try {
      const out = await fn();
      await refresh();
      return out;
    } catch (e) {
      toast.error(errMsg(e));
      return null;
    } finally {
      setBusy(false);
    }
  }, [refresh]);

  const ouvrirCaisse = (montant) => run(async () => {
    await api.post("/caisse/ouvrir", { montant_ouverture: Number(montant || 0) });
    toast.success("Caisse ouverte");
    return true;
  });

  const fermerCaisse = () => run(async () => {
    const r = await api.post("/caisse/fermer");
    toast.success(`Caisse clôturée — Total du jour : ${fmtCFA(r.data.total)}`);
    return true;
  });

  const rouvrirCaisse = (motif = "") => run(async () => {
    await api.post("/caisse/rouvrir", { motif });
    toast.success("Caisse rouverte");
    return true;
  });

  const payer = (paiement, reference) => run(async () => {
    const r = await api.post("/ventes", {
      items: cart.map(({ type, ref_id, nom, prix, quantite, membre_id, abonnement, membre_nom }) => ({
        type, ref_id, nom, prix, quantite, membre_id: membre_id || null, abonnement: abonnement || null, membre_nom: membre_nom || null,
      })),
      paiement,
      reference: paiement === "especes" ? null : reference.trim(),
    });
    toast.success(`Vente validée (${payLabel(paiement)}) — ${fmtCFA(total)}`);
    (r.data.renouvellements || []).forEach((rn) =>
      toast.success(`Abonnement de ${rn.membre_nom} prolongé jusqu'au ${new Date(`${rn.date_fin}T12:00:00`).toLocaleDateString("fr-FR")}`),
    );
    setCart([]);
    return r.data;
  });

  return { data, produits, seances, cart, busy, caisseOuverte, total, addToCart, changeQty, removeLine, ouvrirCaisse, fermerCaisse, rouvrirCaisse, payer };
}
